import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Trash2, ShoppingBag, ArrowRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const Cart = () => {
  const navigate = useNavigate();
  const { cartItems, cartTotal, removeFromCart } = useCart();
  const { user } = useAuth();

  const handleCheckout = () => {
    if (!user) {
      toast.error('Please login to proceed to checkout');
      return;
    }
    navigate('/checkout');
  };

  const handleRemove = async (productId) => {
    try {
      await removeFromCart(productId);
      toast.success('Item removed from cart');
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <div className="flex-1 bg-slate-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-4xl lg:text-5xl font-bold text-primary mb-8 uppercase tracking-tight" data-testid="cart-heading">
            Shopping Cart
          </h1>

          {cartItems.length === 0 ? (
            <div className="bg-white rounded-sm shadow-sm p-12 text-center" data-testid="empty-cart">
              <ShoppingBag className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h2 className="font-heading text-2xl font-bold text-primary mb-2 uppercase">Your cart is empty</h2>
              <p className="text-slate-600 mb-6">Add some products to get started</p>
              <Link to="/products">
                <Button data-testid="continue-shopping-button">Continue Shopping</Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-4">
                {cartItems.map((item) => (
                  <div
                    key={item.product_id}
                    className="bg-white rounded-sm shadow-sm p-4 flex gap-4"
                    data-testid={`cart-item-${item.product_id}`}
                  >
                    <img
                      src={item.product?.images?.[0] || 'https://images.unsplash.com/photo-1615746363486-92cd8c5e0a90?w=200'}
                      alt={item.product?.name}
                      className="w-24 h-24 object-cover rounded-sm"
                    />
                    <div className="flex-1">
                      <h3 className="font-heading text-lg font-bold text-primary mb-1 uppercase">
                        {item.product?.name}
                      </h3>
                      {item.custom_size && (
                        <p className="text-sm text-secondary font-medium mb-1">
                          Size: {item.custom_size}
                        </p>
                      )}
                      <p className="text-sm text-slate-600 mb-2">Quantity: {item.quantity}</p>
                      <p className="text-xl font-bold text-secondary">
                        ₹{(item.product?.price * item.quantity).toFixed(2)}
                      </p>
                    </div>
                    <button
                      onClick={() => handleRemove(item.product_id)}
                      className="text-red-500 hover:text-red-700"
                      data-testid={`remove-item-${item.product_id}`}
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Order Summary */}
              <div>
                <div className="bg-white rounded-sm shadow-sm p-6 sticky top-24" data-testid="order-summary">
                  <h2 className="font-heading text-2xl font-bold text-primary mb-6 uppercase">Order Summary</h2>
                  
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between text-slate-600">
                      <span>Subtotal</span>
                      <span data-testid="cart-subtotal">₹{cartTotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-slate-600">
                      <span>Shipping</span>
                      <span>Calculated at checkout</span>
                    </div>
                    <div className="border-t border-slate-200 pt-3">
                      <div className="flex justify-between text-xl font-bold text-primary">
                        <span>Total</span>
                        <span data-testid="cart-total">₹{cartTotal.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={handleCheckout}
                    className="w-full gap-2"
                    data-testid="checkout-button"
                  >
                    Proceed to Checkout <ArrowRight className="h-5 w-5" />
                  </Button>

                  <Link to="/products">
                    <Button variant="outline" className="w-full mt-3" data-testid="continue-shopping-link">
                      Continue Shopping
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Cart;